import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabaseCoreClient } from '../supabase';
import { useAuth } from '../useAuth';
import type { AppUser } from '../../types/api';
import { queryPolicies } from '../queryPolicies';

/**
 * The current user's RBAC capabilities (cached server-side in
 * core_crm.user_capability by refresh_user_permissions). Super admins hold
 * every capability implicitly. This drives UI gating only — the database
 * enforces the same rules via RLS and guarded report sources, so hiding here
 * is UX rather than the authorization boundary.
 */
export function useMyCapabilities(appUserOverride?: AppUser | null) {
  const { appUser: authenticatedAppUser } = useAuth();
  // A route that has already resolved its identity can supply it here. This
  // avoids a second, independent useAuth startup window where capabilities
  // are briefly queried with no user and an authorized route redirects.
  const appUser = appUserOverride ?? authenticatedAppUser;
  const appUserId = appUser?.id ?? null;
  const isSuperAdmin = appUser?.is_super_admin === true;

  const { data, isLoading } = useQuery({
    queryKey: ['my-capabilities', appUserId],
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabaseCoreClient
        .from('user_capability')
        .select('capability_key')
        .eq('user_id', appUserId as number);
      if (error) throw error;
      return (data ?? []).map((row) => row.capability_key as string);
    },
    enabled: !!appUserId && !isSuperAdmin,
    staleTime: queryPolicies.permissions.staleTime,
    gcTime: queryPolicies.permissions.gcTime,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });

  // Reference-stable: callers memoize AG Grid column definitions on
  // hasCapability, so a fresh closure per render would rebuild every column.
  const capabilities = useMemo(() => new Set(data ?? []), [data]);
  const hasCapability = useCallback(
    (key: string) => isSuperAdmin || capabilities.has(key),
    [capabilities, isSuperAdmin],
  );

  return {
    isLoading: !isSuperAdmin && isLoading,
    isSuperAdmin,
    capabilities,
    hasCapability,
  };
}
